/**
 * Canonical incident type list — single source of truth for the frontend.
 *
 * INCIDENT_TYPES: the ordered list used in the create/edit form and the
 * list-page filter dropdown. Keep in sync with ReportHazard.js option values.
 *
 * incidentTypeToLabel: maps DB-stored snake_case incident_type enum values
 * (written by the backend's _derive_incident_type helper) to human-readable
 * display labels for the list table.
 */

// NOTE: 'Observation', 'Unsafe Act', and 'Unsafe Condition' were removed —
// they belong to the separate Observations module (see
// constants/observationCategories.js), not Incident Management.
export const INCIDENT_TYPES = [
  'Near-Miss',
  'First Aid',
  'Medical Treatment',
  'Restricted Work',
  'Lost Time Injury',
  'Fatality',
  'Property Damage',
  'Environmental',
  'Equipment Failure',
  'Occupational Disease',
  'Occupational Poisoning',
  'Other',
];

// Per-type guidance shown under the Incident Type dropdown in the create and
// edit forms. Keys must stay the exact INCIDENT_TYPES label strings (they
// double as the <option> values in ReportHazard.js / HazardDetail.js).
export const INCIDENT_TYPE_HELP = {
  'Near-Miss': 'An unplanned event that did not result in injury, illness, or damage — but had the potential to do so.',
  'First Aid': 'A minor injury requiring only one-time first-aid treatment (e.g. cleaning, bandaging) with no professional medical care.',
  'Medical Treatment': 'An injury or illness requiring care by a medical professional beyond first aid, without lost workdays or restricted duty.',
  'Restricted Work': 'An injury or illness leaving the person unable to perform one or more routine job functions or work a full shift.',
  'Lost Time Injury': 'A work-related injury or illness causing the person to miss one or more scheduled workdays after the day of the incident.',
  'Fatality': 'A work-related death, whether immediate or occurring later as a result of a work-related injury or illness.',
  'Property Damage': 'Damage to buildings, vehicles, equipment, materials, or third-party property, without personal injury.',
  'Environmental': 'An uncontrolled spill, release, or emission affecting air, water, or land, or a breach of an environmental permit.',
  'Equipment Failure': 'An unexpected breakdown or malfunction of plant, machinery, or tools that caused or could have caused harm or downtime.',
  'Occupational Disease': 'An illness caused by workplace exposure over time, such as noise-induced hearing loss or respiratory disease.',
  'Occupational Poisoning': 'Acute or chronic health effects from workplace exposure to toxic chemicals or hazardous substances.',
  'Other': 'A work-related incident that does not fit the categories above — describe the circumstances clearly in the description.',
};

const INCIDENT_TYPE_LABELS = {
  near_miss: 'Near-Miss',
  first_aid: 'First Aid',
  medical_treatment: 'Medical Treatment',
  lost_time: 'Lost Time Injury',
  fatality: 'Fatality',
  property_damage: 'Property Damage',
  environmental: 'Environmental',
  injury: 'Injury',
  illness: 'Occupational Illness',
  other: 'Other',
};

/**
 * Convert a backend incident_type enum value (e.g. "near_miss") to the
 * human-readable label shown in the UI. Falls back to the raw value when the
 * enum is not in the map — safe for future additions.
 */
export function incidentTypeToLabel(value) {
  if (!value) return 'N/A';
  const key = String(value).toLowerCase().trim();
  return INCIDENT_TYPE_LABELS[key] || value;
}
